import React from 'react';
import { Link } from 'react-router-dom';
import { LuCar, LuChevronRight } from 'react-icons/lu';
import Card from '../../ui/Card/Card';
import Badge from '../../ui/Badge/Badge';
import {
  vehicleDisplayFor,
  VEHICLE_DISPLAY_FALLBACK,
} from '../../../utils/vehicleDisplay';
import './VehicleCard.less';

const BILLING_LABEL = {
  PUBLIC_CARD:    { label: 'Pay by card',       tone: 'neutral' },
  DISCOUNTED_CARD:{ label: 'Discounted card',   tone: 'info' },
  FLEET_INVOICE:  { label: 'Account billed',    tone: 'success' },
  FREE:           { label: 'Free',              tone: 'success' },
};

/**
 * VehicleCard — renders one row of the user's vehicles list.
 *
 * Consumes the user-safe DTO returned by `GET /api/vehicles/mine`:
 *   { id, licensePlate, plateNormalized, displayName?, make?,
 *     model?, year?, color?, status, billingMode, createdAt }.
 * All make/model/year/color fields are optional — the card only
 * renders lines it actually has data for; nothing is invented.
 */
function VehicleCard({ vehicle, to }) {
  const billing = vehicle.billingMode && BILLING_LABEL[vehicle.billingMode];
  const displayPlate = vehicle.licensePlate || vehicle.plateNormalized || '—';
  const nickname = vehicle.displayName?.trim() || null;

  // Canonical vehicle identity — prefer the server-computed
  // `displayVehicle` when the DTO carries it (newer deployments),
  // fall back to a locally computed make/model/year string, then
  // to the stable "Vehicle details unavailable" fallback. Nickname
  // is NEVER a fallback for the identity — that was the exact bug
  // being eliminated in this stack-wide standardisation.
  const canonical = vehicleDisplayFor(vehicle);
  const canonicalIsFallback = canonical === VEHICLE_DISPLAY_FALLBACK;

  // Card layout: plate is the primary row-identity (unchanged);
  // the actual vehicle (make + model + year) drops beneath it as
  // the primary vehicle line; nickname becomes the secondary
  // labelled line — a switch from the previous order where nickname
  // came first. When we truly have no make/model, the nickname can
  // promote back to the primary vehicle line so the row still
  // renders something recognisable.
  const primaryVehicleLine = canonicalIsFallback ? nickname : canonical;
  const nicknameLine =
    !canonicalIsFallback && nickname && nickname !== canonical
      ? nickname
      : null;

  const isInactive = vehicle.status === 'inactive';

  const content = (
    <div className="gc-vehicle-card-inner">
      <div className="gc-vehicle-card-lead">
        <span className="gc-vehicle-card-icon" aria-hidden="true"><LuCar /></span>
        <div className="gc-vehicle-card-text">
          <div className="gc-vehicle-card-row">
            <span className="gc-vehicle-card-plate">{displayPlate}</span>
            {isInactive && <Badge tone="warning">Inactive</Badge>}
          </div>
          {primaryVehicleLine && <p className="gc-vehicle-card-name">{primaryVehicleLine}</p>}
          {nicknameLine && <p className="gc-vehicle-card-meta-line">Nickname: {nicknameLine}</p>}
          {(billing || vehicle.color) && (
            <div className="gc-vehicle-card-meta">
              {billing && <Badge tone={billing.tone}>{billing.label}</Badge>}
              {vehicle.color && <span className="gc-vehicle-card-colour">{vehicle.color}</span>}
            </div>
          )}
        </div>
      </div>
      {to && <LuChevronRight aria-hidden="true" className="gc-vehicle-card-chevron" />}
    </div>
  );

  if (to) {
    return (
      <Card as={Link} to={to} interactive className="gc-vehicle-card">
        {content}
      </Card>
    );
  }
  return <Card className="gc-vehicle-card">{content}</Card>;
}

export default VehicleCard;
